/**
 * Cliente Firebase Cloud Messaging (Web Push).
 *
 * Responsabilidades:
 *   - Carregar config pública do backend (edge function `fcm-config`)
 *   - Inicializar app Firebase + registrar o Service Worker
 *   - Solicitar permissão, obter token FCM e registrá-lo no backend
 *   - Tratar mensagens em foreground (toast)
 *   - Desregistrar token quando o usuário desativa push
 *   - Detectar rotação de token e desregistrar o anterior desta
 *     instância antes de registrar o novo (evita acúmulo de tokens
 *     ativos para o mesmo dispositivo físico).
 *
 * CHANGELOG:
 *   v1.2.0 - 2026-06-05 - Envia p_tenant_hint no registro do device e adiciona
 *            trava curta local para evitar registros concorrentes multi-aba.
 *   v1.1.0 - 2026-06-05 - Rotação de token: persiste último token em
 *            localStorage por origin; chama unregister_user_device do
 *            token anterior antes de registrar o novo; envia p_origin
 *            para o backend usar no cálculo de device_fingerprint.
 */
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
  deleteToken,
  type Messaging,
} from 'firebase/messaging';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { showToast } from '@/lib/toast';

const logger = createLogger('fcm');
const SW_PATH = '/firebase-messaging-sw.js';
const REGISTER_LOCK_TTL_MS = 15_000;

/**
 * Chave por-origin onde guardamos o último token FCM registrado por esta
 * instância de navegador. Permite detectar rotação de token (Safari/PWA)
 * e desregistrar o token anterior antes de registrar o novo — evita
 * acúmulo de tokens ativos para o mesmo dispositivo físico.
 */
function lastTokenStorageKey(): string {
  const origin = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
  return `fcm:lastToken@${origin}`;
}

function readLastToken(): string | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage.getItem(lastTokenStorageKey()) : null;
  } catch {
    return null;
  }
}

function writeLastToken(token: string): void {
  try {
    if (typeof window !== 'undefined') window.localStorage.setItem(lastTokenStorageKey(), token);
  } catch {
    // ignore (modo privado, storage cheio, etc.)
  }
}

/**
 * Limpa o cache local do último token registrado neste origin.
 * Usado quando o login atual reivindica o navegador de outro usuário: mesmo
 * que o token FCM físico não tenha mudado, precisamos forçar novo registro no
 * backend para mover o vínculo para o usuário/tenant autenticado agora.
 */
export function clearLocalFcmTokenRegistration(): void {
  try {
    if (typeof window !== 'undefined') window.localStorage.removeItem(lastTokenStorageKey());
  } catch {
    // ignore (modo privado, storage cheio, etc.)
  }
}

/**
 * Trava curta por origin para evitar que duas abas/instâncias registrem o
 * mesmo token ao mesmo tempo. A trava expira sozinha para não prender o usuário
 * se o navegador fechar no meio da operação.
 */
function registerLockStorageKey(): string {
  const origin = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
  return `fcm:registering@${origin}`;
}

function acquireRegisterLock(): (() => void) | null {
  if (typeof window === 'undefined') return () => undefined;
  try {
    const key = registerLockStorageKey();
    const now = Date.now();
    const current = Number(window.localStorage.getItem(key) || 0);
    if (current && now - current < REGISTER_LOCK_TTL_MS) return null;
    window.localStorage.setItem(key, String(now));
    return () => {
      try { window.localStorage.removeItem(key); } catch { /* noop */ }
    };
  } catch {
    return () => undefined;
  }
}

/**
 * Helper: registra o token no backend e, se houver um token anterior
 * persistido localmente que mudou, desregistra o antigo PRIMEIRO.
 * Centraliza o ciclo "rotacionou token → limpa o antigo desta instância".
 */
async function registerTokenWithRotation(token: string, tenantId?: string | null): Promise<boolean> {
  const releaseLock = acquireRegisterLock();
  if (!releaseLock) {
    logger.warn('registro FCM ignorado: outra aba já está registrando o dispositivo');
    return readLastToken() === token;
  }

  const previousToken = readLastToken();

  try {
    if (previousToken && previousToken !== token) {
      try {
        await supabase.rpc('unregister_user_device', { p_push_token: previousToken });
        logger.debug('token FCM anterior desregistrado (rotação detectada)');
      } catch (err) {
        // não bloqueia; o backend ainda vai deduplicar pela fingerprint
        logger.warn('falha ao desregistrar token anterior', err);
      }
    }

    const origin = typeof window !== 'undefined' ? window.location.hostname : null;
    const { error } = await supabase.rpc('register_user_device', {
      p_push_token: token,
      p_device_type: detectDeviceType(),
      p_device_name: detectDeviceName(),
      p_user_agent: navigator.userAgent.slice(0, 500),
      p_provider: 'fcm',
      p_tenant_hint: tenantId ?? null,
      p_origin: origin,
    } as any);

    if (error) {
      logger.error('Erro ao registrar device', error);
      return false;
    }
    writeLastToken(token);
    return true;
  } finally {
    releaseLock();
  }
}

interface FcmConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  messagingSenderId: string;
  appId: string;
  vapidKey: string;
}

interface FcmConfigResponse {
  enabled: boolean;
  config: FcmConfig;
}

let _config: FcmConfig | null = null;
let _app: FirebaseApp | null = null;
let _messaging: Messaging | null = null;
let _swReg: ServiceWorkerRegistration | null = null;
let _supported: boolean | null = null;
let _foregroundBound = false;

export async function isPushSupported(): Promise<boolean> {
  if (_supported !== null) return _supported;
  if (typeof window === 'undefined') return false;
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    _supported = false;
    return false;
  }
  try {
    _supported = await isSupported();
  } catch {
    _supported = false;
  }
  return _supported;
}

export function getPermissionState(): NotificationPermission | 'unsupported' {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/**
 * Verifica se o dispositivo está atualmente inscrito para receber push.
 * Fonte de verdade real do estado do toggle — independente de Notification.permission,
 * que continua 'granted' mesmo após o usuário desativar o push pelo app.
 *
 * Regras:
 *  - Se push não suportado ou permissão != 'granted' → false
 *  - Se não houver Service Worker registrado em SW_PATH → false
 *  - Se pushManager.getSubscription() não retornar subscription ativa → false
 *  - Caso contrário → true
 */
export async function isDeviceSubscribed(): Promise<boolean> {
  try {
    if (!(await isPushSupported())) return false;
    if (getPermissionState() !== 'granted') return false;
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false;

    // Usa registration já carregado, ou tenta obter o existente sem registrar de novo
    const reg = _swReg ?? (await navigator.serviceWorker.getRegistration(SW_PATH));
    if (!reg) return false;

    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}

async function loadConfig(): Promise<FcmConfig | null> {
  if (_config) return _config;
  try {
    const { data, error } = await supabase.functions.invoke<FcmConfigResponse>('fcm-config', {
      method: 'GET',
    });
    if (error) {
      logger.error('Erro ao carregar config FCM', error);
      return null;
    }
    if (!data?.enabled || !data.config?.apiKey) {
      logger.warn('FCM não habilitado no backend (secrets ausentes)');
      return null;
    }
    _config = data.config;
    return _config;
  } catch (err) {
    logger.error('Falha ao buscar fcm-config', err);
    return null;
  }
}

async function ensureMessaging(): Promise<Messaging | null> {
  if (_messaging) return _messaging;
  if (!(await isPushSupported())) return null;

  const config = await loadConfig();
  if (!config) return null;

  if (!_app) {
    _app = initializeApp({
      apiKey: config.apiKey,
      authDomain: config.authDomain,
      projectId: config.projectId,
      messagingSenderId: config.messagingSenderId,
      appId: config.appId,
    });
  }

  if (!_swReg) {
    // Sempre usar o registration específico do worker FCM — nunca pegar
    // um registration genérico do escopo /, pois pode haver outro worker
    // (ex.: Workbox kill-switch /sw.js) controlando a página.
    const existing = await navigator.serviceWorker.getRegistration(SW_PATH);
    _swReg = existing ?? (await navigator.serviceWorker.register(SW_PATH, { scope: '/' }));
    // A config Firebase já está embutida no firebase-messaging-sw.js (v3.0.0).
    // FCM_INIT é enviado apenas por compatibilidade caso o SW publicado ainda
    // seja a versão antiga que dependia de postMessage.
    const target = _swReg.active || _swReg.waiting || _swReg.installing;
    const sendConfig = (sw: ServiceWorker) => {
      try {
        sw.postMessage({
          type: 'FCM_INIT',
          config: {
            apiKey: config.apiKey,
            authDomain: config.authDomain,
            projectId: config.projectId,
            messagingSenderId: config.messagingSenderId,
            appId: config.appId,
          },
        });
      } catch {
        // ignore — SW novo não precisa do postMessage
      }
    };
    if (target) {
      if (target.state === 'activated') sendConfig(target);
      else target.addEventListener('statechange', () => {
        if (target.state === 'activated') sendConfig(target);
      });
    }
  }

  _messaging = getMessaging(_app);

  if (!_foregroundBound) {
    _foregroundBound = true;
    onMessage(_messaging, (payload) => {
      logger.info('mensagem em foreground', payload);
      const title = payload.notification?.title || payload.data?.title || 'Notificação';
      const body = payload.notification?.body || payload.data?.body || '';
      showToast.info(title, body);
    });
  }

  return _messaging;
}

export async function enablePush(tenantId?: string | null): Promise<string | null> {
  if (!(await isPushSupported())) {
    showToast.error('Este navegador não suporta notificações push.');
    return null;
  }

  const config = await loadConfig();
  if (!config) {
    showToast.error('Push não configurado no servidor.');
    return null;
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    showToast.warning('Permissão de notificação negada.');
    return null;
  }

  const messaging = await ensureMessaging();
  if (!messaging) return null;

  try {
    const token = await getToken(messaging, {
      vapidKey: config.vapidKey,
      serviceWorkerRegistration: _swReg ?? undefined,
    });
    if (!token) {
      showToast.error('Não foi possível obter token de push.');
      return null;
    }

    const ok = await registerTokenWithRotation(token, tenantId);
    if (!ok) {
      // CRÍTICO: se o backend rejeitou o token, desfaz a inscrição local
      // para que o estado do toggle volte coerentemente para "desligado"
      // (isDeviceSubscribed retornará false) e o usuário possa tentar de novo.
      try {
        await deleteToken(messaging);
      } catch (e) {
        logger.warn('falha ao remover token local após erro de registro', e);
      }
      try {
        window.localStorage.removeItem(lastTokenStorageKey());
      } catch {
        /* noop */
      }
      showToast.error('Falha ao salvar dispositivo no servidor.');
      return null;
    }

    showToast.success('Notificações push ativadas neste dispositivo.');
    return token;
  } catch (err) {
    logger.error('Erro ao habilitar push', err);
    showToast.error('Erro ao ativar push.');
    return null;
  }
}

export async function disablePush(): Promise<boolean> {
  const messaging = await ensureMessaging();
  if (!messaging) return false;

  try {
    const config = await loadConfig();
    let currentToken: string | null = null;
    if (config) {
      try {
        currentToken = await getToken(messaging, {
          vapidKey: config.vapidKey,
          serviceWorkerRegistration: _swReg ?? undefined,
        });
      } catch {
        // ignore
      }
    }

    await deleteToken(messaging).catch(() => null);

    if (currentToken) {
      await supabase.rpc('unregister_user_device', { p_push_token: currentToken });
      // Limpa o cache local para que o próximo enablePush não tente
      // desregistrar um token que já não existe mais.
      try { window.localStorage.removeItem(lastTokenStorageKey()); } catch { /* noop */ }
    }
    showToast.success('Notificações push desativadas neste dispositivo.');
    return true;
  } catch (err) {
    logger.error('Erro ao desativar push', err);
    return false;
  }
}

export async function silentRefreshToken(options: { forceRegister?: boolean } = {}): Promise<void> {
  if (!(await isPushSupported())) return;
  if (getPermissionState() !== 'granted') return;
  const config = await loadConfig();
  if (!config) return;
  const messaging = await ensureMessaging();
  if (!messaging) return;
  try {
    const token = await getToken(messaging, {
      vapidKey: config.vapidKey,
      serviceWorkerRegistration: _swReg ?? undefined,
    });
    if (token) {
      // Curto-circuito: se o token não mudou desde o último registro
      // bem-sucedido nesta instância, evitamos o round-trip ao backend.
      // Quando forceRegister=true, estamos em troca de usuário/tenant no mesmo
      // navegador e o vínculo precisa ser regravado mesmo com token idêntico.
      if (!options.forceRegister && readLastToken() === token) return;
      await registerTokenWithRotation(token);
    }
  } catch (err) {
    logger.debug('silentRefreshToken falhou', err);
  }
}

/**
 * Detecta nome amigável do dispositivo incluindo o domínio (origin) e se
 * está rodando como PWA instalado. Isso facilita identificar no banco se o
 * token veio do PWA dev (dev.uaizychat.com.br) ou do PWA oficial
 * (app.uaizychat.com.br) — push é vinculado ao origin exato.
 */
function detectDeviceName(): string {
  const ua = navigator.userAgent;
  let base = 'Web';
  if (/iPhone|iPad|iPod/i.test(ua)) base = 'iOS';
  else if (/Android/i.test(ua)) base = 'Android';
  else if (/Mac/i.test(ua)) base = 'macOS';
  else if (/Windows/i.test(ua)) base = 'Windows';
  else if (/Linux/i.test(ua)) base = 'Linux';

  // Detecta modo standalone (PWA instalado na home screen)
  const isStandalone =
    (typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(display-mode: standalone)').matches) ||
    // iOS Safari expõe via navigator.standalone
    (typeof navigator !== 'undefined' && (navigator as any).standalone === true);

  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const suffix = isStandalone ? ' PWA' : '';
  return host ? `${base}${suffix} - ${host}` : `${base}${suffix}`;
}

/**
 * Classifica o tipo de dispositivo de forma mais granular para o banco.
 * Mantém compatibilidade com o valor antigo ('web') para callers externos.
 */
function detectDeviceType(): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isStandalone =
    (typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(display-mode: standalone)').matches) ||
    (typeof navigator !== 'undefined' && (navigator as any).standalone === true);

  if (/iPhone|iPad|iPod/i.test(ua)) return isStandalone ? 'ios-pwa' : 'ios-web';
  if (/Android/i.test(ua)) return isStandalone ? 'android-pwa' : 'android-web';
  return isStandalone ? 'desktop-pwa' : 'web';
}
